<template>
  <div>
    <vs-tooltip :text="$t('orgDashboard.talentPool.downloadResume')" v-if="email">
      <vs-button
        class="download-btn"
        color="primary"
        type="flat"
        icon-pack="feather"
        icon="icon-download"
        size="small"
        :disabled="isLoading"
        @click.stop="openResumePreview"
      >
      </vs-button>
    </vs-tooltip>

    <vs-tooltip :text="$t('orgDashboard.talentPool.noEmailOnRecord')" v-else>
      <vs-button
        color="dark"
        type="border"
        icon-pack="feather"
        icon="icon-eye-off"
        size="small"
        disabled
      >
      </vs-button>
    </vs-tooltip>

    <vs-popup
      class="resume-popup"
      :title="popupTitle"
      :active.sync="isPopupActive"
    >
      <div v-if="viewMode === 'loading'" class="state-container">
        <div class="con-vs-loading"><div class="vs-loading"></div></div>
        <span>{{ $t('orgDashboard.talentPool.loading_Resume') }}.</span>
      </div>

      <div v-if="viewMode === 'preview'" class="preview-wrapper">
        <iframe
          v-if="isPreviewable"
          :src="previewUrl"
          width="100%"
          height="100%"
        ></iframe>

        <div v-else class="state-container download-prompt">
          <span class="download-icon">📁</span>
          <h4>{{ $t('orgDashboard.talentPool.previewNotAvailable') }}</h4>
          <p>This resume is a <strong>{{ friendlyFileType }}</strong> file.</p>
          <p>Direct preview is not supported for this format. Please download it to view.</p>
          <vs-button
            color="primary"
            icon-pack="feather"
            icon="icon-download"
            :href="previewUrl"
            :download="fileName"
          >
            {{ $t('orgDashboard.talentPool.download') }} {{ fileName }}
          </vs-button>
        </div>
      </div>
      
      <div v-if="viewMode === 'upload'" class="state-container upload-form">
        <div class="upload-text">
          <h4>{{ $t('orgDashboard.talentPool.Resume_not_found') }}</h4>
          <p>{{ $t('orgDashboard.talentPool.we_could_not_find_resume') }} <strong>{{ email }}</strong>. {{ $t('orgDashboard.talentPool.pleaseSelectFile') }}.</p>
        </div>
        
        <transition name="fade">
          <div v-if="uploadMessage.text" :class="['upload-message', uploadMessage.type]">
            <span v-if="uploadMessage.type === 'success'" class="message-icon">✓</span>
            <span v-else-if="uploadMessage.type === 'error'" class="message-icon">✗</span>
            <span>{{ uploadMessage.text }}</span>
          </div>
        </transition>

        <div class="upload-area-wrapper">
          <input 
            type="file" 
            @change="handleFileSelect" 
            ref="fileInput" 
            accept=".pdf,.docx,.doc,.txt,.dotx,.dot"
            hidden 
          />
          
          <template v-if="!selectedFile">
            <vs-button 
              type="border" 
              color="warning"
              @click="$refs.fileInput.click()"
              :disabled="isUploading"
              class="choose-file-btn"
            >
              {{ $t('orgDashboard.talentPool.choosefile') }}
            </vs-button>
          </template>
          
          <div v-else class="selected-file-box">
            <div class="file-info">
              <span class="file-icon">📄</span>
              <span class="file-name" :title="selectedFile.name">{{ selectedFile.name }}</span>
            </div>
            <vs-button 
              color="danger" 
              type="flat" 
              icon-pack="feather" 
              icon="icon-x" 
              size="small"
              @click="removeSelectedFile"
              class="remove-file-btn"
            ></vs-button>
          </div>
        </div>

        <div class="upload-actions">
          <vs-button
            color="warning"
            icon-pack="feather"
            icon="icon-upload"
            :disabled="!selectedFile || isUploading"
            @click="handleUpload"
          >
            <span v-if="!isUploading">{{ $t('orgDashboard.talentPool.verify') }}</span>
            <span v-else>{{ $t('orgDashboard.talentPool.uploading') }}</span>
          </vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
const API_BASE_URL = process.env.VUE_APP_API_URL;

const PREVIEWABLE_MIME_TYPES = [
  'application/pdf',
  'text/plain',
];

const MIME_TYPE_FRIENDLY_NAMES = {
  'application/msword': 'Microsoft Word Document (.doc)',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Microsoft Word Document (.docx)',
  'application/pdf': 'PDF Document',
  'text/plain': 'Text File',
  'default': 'File'
};

export default {
  props: {
    email: { type: String, required: true },
  },
  data() {
    return {
      isPopupActive: false,
      isLoading: false,
      previewUrl: null,        
      isPreviewable: false,   
      fileName: 'resume',      
      friendlyFileType: 'File',  
      viewMode: 'loading',
      selectedFile: null,
      isUploading: false,
      uploadMessage: { text: '', type: null },
    };
  },
  computed: {
    popupTitle() {
      if (this.viewMode === 'upload') return this.$t('orgDashboard.talentPool.UploadNewResume');
      if (this.viewMode === 'preview' && !this.isPreviewable) return this.$t('orgDashboard.talentPool.downloadResume');
      return this.$t('orgDashboard.talentPool.Resume_Preview');
    },
    resumeFetchUrl() {
      return `${API_BASE_URL}/cv/${encodeURIComponent(this.email)}`;
    },
    resumeUploadUrl() {
      return `${API_BASE_URL}/upload-validated-resume/`; 
    }
  },
  methods: {
    async openResumePreview() {
      if (this.isLoading) return;
      this.isLoading = true;
      this.isPopupActive = true;
      this.viewMode = 'loading';

      try {
        const response = await fetch(this.resumeFetchUrl);
        if (!response.ok) {
            throw new Error('Resume not found on server.');
        }

        const contentType = response.headers.get('content-type') || '';
        const mimeType = contentType.split(';')[0].trim();
        
        const disposition = response.headers.get('content-disposition');
        if (disposition && disposition.includes('filename=')) {
          const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
          this.fileName = filenameMatch && filenameMatch[1] ? filenameMatch[1] : 'resume';
        }
        
        this.isPreviewable = PREVIEWABLE_MIME_TYPES.includes(mimeType);

        this.friendlyFileType = MIME_TYPE_FRIENDLY_NAMES[mimeType] || MIME_TYPE_FRIENDLY_NAMES['default'];

        const fileBlob = await response.blob();
        const blobUrl = URL.createObjectURL(fileBlob);
        this.previewUrl = (mimeType === 'application/pdf') ? `${blobUrl}#view=FitH` : blobUrl;
        
        this.viewMode = 'preview';

      } catch (error) {
        this.viewMode = 'upload';
      } finally {
        this.isLoading = false;
      }
    },
    handleFileSelect(event) {
      this.uploadMessage = { text: '', type: null };
      this.selectedFile = event.target.files[0];
    },
    async handleUpload() {
      if (!this.selectedFile || this.isUploading) return;
      this.isUploading = true;
      this.uploadMessage = { text: '', type: null }; 

      const formData = new FormData();
      formData.append('file', this.selectedFile);
      formData.append('user_email', this.email);

      try {
        const response = await fetch(this.resumeUploadUrl, { method: 'POST', body: formData });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'The upload was rejected by the server.');
        }

        this.uploadMessage = { text: 'Success! Your resume has been updated.', type: 'success' };
        
        setTimeout(() => {
          this.openResumePreview();
        }, 1500);

      } catch (error) {
        this.uploadMessage = { text: error.message, type: 'error' };
      } finally {
        this.isUploading = false;
      }
    },
      removeSelectedFile() {
      this.selectedFile = null;
      this.$refs.fileInput.value = '';  
      this.uploadMessage = { text: '', type: null };  
    },
  },
  watch: {
    isPopupActive(isActive) {
      if (!isActive) {
        if (this.pdfUrl) {
          URL.revokeObjectURL(this.pdfUrl.split('#')[0]);
          this.pdfUrl = null;
        }
        this.selectedFile = null;
        this.uploadMessage = { text: '', type: null };
      }
    }
  }
};
</script>

<style scoped>
.resume-popup >>> .vs-popup { width: 900px; max-width: 95%; }
.resume-popup >>> .vs-popup--content { padding: 0; height: 85vh; overflow: hidden; }
.preview-wrapper, iframe { width: 100%; height: 100%; border: none; }

.state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  height: 100%;
  box-sizing: border-box;
}

.upload-form {
  gap: 25px;
  padding: 40px 30px;
  max-width: 500px;
  margin: 0 auto;
}

.upload-text h4 { 
  font-size: 1.8rem; 
  font-weight: 600; 
  margin-bottom: 15px; 
  color: #2c3e50;
}
.upload-text p { 
  font-size: 1rem; 
  color: #666; 
  line-height: 1.6; 
  margin: 0;
}

.upload-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 8px;
  width: 100%;
  max-width: 450px;
  font-weight: 500;
  animation: slideIn 0.3s ease-out;
}
@keyframes slideIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
.message-icon { font-size: 1.2rem; font-weight: bold; }
.upload-message.error { background-color: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; }
.upload-message.success { background-color: #d4edda; color: #155724; border: 1px solid #c3e6cb; }

.upload-area-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  width: 100%;
}
.choose-file-btn { min-width: 150px; }

.selected-file-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f9fa;
  border: 2px dashed #ffc107;
  border-radius: 8px;
  padding: 10px 15px;
  width: 100%;
  max-width: 450px;
  box-sizing: border-box;
  min-height: 50px;
}

.file-info {
  display: flex;
  align-items: center;
  min-width: 0; 
  flex: 1;
}

.file-icon {
  font-size: 1.5rem;
  margin-right: 12px;
}

.file-name {
  font-size: 0.95rem;
  color: #495057;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

.remove-file-btn {
  margin-left: 10px;
  min-width: 28px !important;
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.remove-file-btn :deep(.vs-icon) {
  margin: 0 !important;
}

.upload-actions {
  width: 100%;
  display: flex;
  justify-content: center;
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s, transform 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; transform: translateY(-10px); }

.state-container span { margin-top: 20px; font-size: 1.2rem; color: #555; }
.con-vs-loading { position: relative; }
.vs-loading {
  display: block; width: 40px; height: 40px; border-radius: 50%;
  border: 3px solid transparent; border-top-color: rgba(0, 0, 0, 0.7);
  animation: spin 1s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* FIXED download-btn CSS */
.download-btn {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  outline: none !important;
  color: #ffc107 !important;

  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  padding: 0 !important;

  border-radius: 50% !important; 

  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  transition: all 0.25s ease !important;
}

/* Force hiding of Vuesax internal background layers that bleed through as a ring */
.download-btn :deep(.vs-button-backgroundx),
.download-btn :deep(.vs-button-linex) {
  display: none !important;
}

.download-btn :deep(.vs-icon) {
  color: #ffc107 !important;
  margin: 0 !important;
  transition: all 0.25s ease !important;
}

.download-btn:hover {
  background: #ffc107 !important;
  color: white !important;
}

.download-btn:hover :deep(.vs-icon) {
  color: white !important;
}
</style>